// ============================================
// MyBooks.jsx - MY UPLOADED BOOKS SECTION
// Save as: src/MyBooks.jsx
// ============================================

import React, { useState } from 'react';
import {
  Book,
  Upload
} from 'lucide-react';
import RequestReviewModal from './RequestReviewModal';

const TABS = [
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' }
];

const MyBooks = ({
  currentUser,
  setCurrentUser,
  books,
  reviews,
  bookProgress,
  setView
}) => {
  const [activeTab, setActiveTab] = useState('pending');
  const [expandedReasons, setExpandedReasons] = useState(new Set());
  const [requestReviewBook, setRequestReviewBook] = useState(null);

  // ============================================
  // HELPER FUNCTIONS
  // ============================================

  const getUserBooks = () => {
    if (!books || !Array.isArray(books)) return [];
    if (!currentUser?.id) return [];

    // Exclude review-request copies (they have masterBookId set) — those
    // belong only in Browse, not the author's dashboard. Only master books.
    return books.filter(b => b.uploadedBy === currentUser.id && !b.masterBookId);
  };

  const toggleExpanded = (bookId) => {
    setExpandedReasons(prev => {
      const next = new Set(prev);
      if (next.has(bookId)) {
        next.delete(bookId);
      } else {
        next.add(bookId);
      }
      return next;
    });
  };

  const renderRejectionReason = (book) => {
    const reason = book.rejectionReason || 'No reason provided.';
    const words = reason.trim().split(/\s+/).filter(Boolean);
    const isLong = words.length > 10;

    if (!isLong) {
      return <p className="text-sm text-gray-700">{reason}</p>;
    }

    const isExpanded = expandedReasons.has(book.id);

    if (isExpanded) {
      return (
        <p className="text-sm text-gray-700">
          {reason}{' '}
          <button
            type="button"
            onClick={() => toggleExpanded(book.id)}
            className="text-indigo-600 text-xs font-medium underline"
          >
            see less
          </button>
        </p>
      );
    }

    const truncated = words.slice(0, 10).join(' ');
    return (
      <p className="text-sm text-gray-700">
        {truncated}...{' '}
        <button
          type="button"
          onClick={() => toggleExpanded(book.id)}
          className="text-indigo-600 text-xs font-medium underline"
        >
          see more
        </button>
      </p>
    );
  };

  // ============================================
  // SPLIT BOOKS BY STATUS
  // ============================================

  const myBooks = getUserBooks();
  const visibleBooks = myBooks.filter(b => b.status !== 'deleted');

  const pendingBooks = visibleBooks.filter(b => !b.status || b.status === 'pending');
  const approvedBooks = visibleBooks.filter(b => b.status === 'approved');
  const rejectedBooks = visibleBooks.filter(b => b.status === 'rejected');

  const tabCounts = {
    pending: pendingBooks.length,
    approved: approvedBooks.length,
    rejected: rejectedBooks.length
  };

  const tabBooks = {
    pending: pendingBooks,
    approved: approvedBooks,
    rejected: rejectedBooks
  };

  const activeBooks = tabBooks[activeTab];

  // ============================================
  // CARD RENDERERS
  // ============================================

  const renderCoverAndTitle = (book) => (
    <>
      {book.coverImage && (
        <img
          src={book.coverImage}
          alt={book.title}
          className="w-full h-40 object-cover rounded-lg mb-3"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      )}
      <h3 className="font-semibold text-lg mb-1">{book.title}</h3>
      <p className="text-xs text-gray-600 mb-3">by {book.author}</p>
    </>
  );

  const renderPendingCard = (book) => (
    <div key={book.id} className="bg-white rounded-xl p-4 shadow hover:shadow-lg transition">
      {renderCoverAndTitle(book)}
      <span className="inline-block px-3 py-1 bg-yellow-100 text-yellow-800 text-xs font-bold rounded-full">
        ⏳ Awaiting admin approval
      </span>
    </div>
  );

  const renderApprovedCard = (book) => {
    // Real counters computed from this master's copies (Piece 6) — a copy is
    // any book in the full `books` array with masterBookId === book.id.
    const copies = (books || []).filter(b => b.masterBookId === book.id);
    const requestedCount = copies.length;
    const completedCount = copies.filter(c => (c.reviewsReceived || 0) >= 1).length;
    const inBrowseCount = copies.filter(c =>
      c.reviewRequested === true &&
      c.underReview !== true &&
      (c.reviewsReceived || 0) < 1
    ).length;
    const currentlyReadingCount = (bookProgress || []).filter(bp =>
      copies.some(c => c.id === bp.bookId)
    ).length;

    return (
      <div key={book.id} className="bg-white rounded-xl p-4 shadow hover:shadow-lg transition">
        {renderCoverAndTitle(book)}

        <div className="grid grid-cols-2 gap-2 text-sm text-gray-700 mb-3">
          <div> Requested: {requestedCount}</div>
          <div> Completed: {completedCount}</div>
          <div> In Browse: {inBrowseCount}</div>
          
        </div>

        <button
          type="button"
          onClick={() => setRequestReviewBook(book)}
          className="w-full bg-indigo-600 text-white py-2 rounded-lg hover:bg-indigo-700 transition text-sm font-semibold"
        >
          REQUEST REVIEW
        </button>
      </div>
    );
  };

  const renderRejectedCard = (book) => (
    <div key={book.id} className="bg-white rounded-xl p-4 shadow hover:shadow-lg transition">
      {renderCoverAndTitle(book)}
      <div className="bg-red-50 border border-red-200 rounded-lg p-3">
        <p className="text-xs font-semibold text-red-700 mb-1">Rejection reason</p>
        {renderRejectionReason(book)}
      </div>
    </div>
  );

  const EMPTY_MESSAGES = {
    pending: 'No books pending approval.',
    approved: 'No approved books yet.',
    rejected: 'No rejected books.'
  };

  const CARD_RENDERERS = {
    pending: renderPendingCard,
    approved: renderApprovedCard,
    rejected: renderRejectedCard
  };

  // ============================================
  // RENDER
  // ============================================

  return (
    <div>
      {/* ============================================ */}
      {/* UPLOAD BUTTON */}
      {/* ============================================ */}
      <div className="mb-6">
        <button
          onClick={() => setView('upload')}
          className="bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700 transition flex items-center gap-2"
        >
          <Upload className="w-4 h-4" />
          Upload New Book
        </button>
      </div>

      <h2 className="text-2xl font-bold mb-4">My Uploaded Books</h2>

      {/* ============================================ */}
      {/* TABS */}
      {/* ============================================ */}
      <div className="flex gap-2 overflow-x-auto border-b border-gray-200 mb-6">
        {TABS.map(tab => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-3 whitespace-nowrap font-semibold text-sm border-b-2 transition ${
              activeTab === tab.key
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
            <span
              className={`inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 rounded-full text-xs font-bold ${
                activeTab === tab.key ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'
              }`}
            >
              {tabCounts[tab.key]}
            </span>
          </button>
        ))}
      </div>

      {/* ============================================ */}
      {/* ACTIVE TAB BOOK GRID */}
      {/* ============================================ */}
      {activeBooks.length === 0 ? (
        <div className="bg-white rounded-xl p-6 text-center shadow">
          <Book className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600">{EMPTY_MESSAGES[activeTab]}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {activeBooks.map(book => CARD_RENDERERS[activeTab](book))}
        </div>
      )}

      {/* ============================================ */}
      {/* REQUEST REVIEW WIZARD */}
      {/* ============================================ */}
      {requestReviewBook && (
        <RequestReviewModal
          book={requestReviewBook}
          currentUser={currentUser}
          onClose={() => setRequestReviewBook(null)}
          onSuccess={(newBalance) => {
            if (typeof newBalance === 'number' && setCurrentUser) {
              setCurrentUser(prev => ({ ...prev, coins: newBalance }));
            }
          }}
        />
      )}
    </div>
  );
};

export default MyBooks;
